import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Calculator, Download } from 'lucide-react';
import { losApi } from '../../losApi';
import { downloadWithAuth } from '../../api';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { Input } from '../../../components/ui/input';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import MoneyInput from '../../shared/MoneyInput';
import NumberInput from '../../shared/NumberInput';
import StateSelect from '../../shared/StateSelect';
import { ModuleHeader, Fld } from '../shared/formFields';
import { LINE_SECTIONS, PREPAID_ROWS, ESCROW_ROWS, GOVERNMENT_PROGRAMS, hasFees, countsTowardQm, isMortgageInsurance, hasMiAtClosing } from '../shared/leFees';
import { AriveLockNote, AriveLocked, isAriveManaged, toastAriveConflict } from '../../ariveManaged';

// The loan file's fees are the one fee source (ticket 192, grill 186 D8): line items,
// prepaids, escrow and lender credit are edited here; totals and cash to close come
// from the server (loan_fees.summary) for the unsaved draft.

const usd = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const num = (v) => (v == null || v === '' ? 0 : Number(v));
const PREVIEW_DELAY_MS = 400;

function FeeLines({ title, field, items, onChange, testId }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { name: '', amount: 0, paid_to: '', borrower_paid: true, is_apr_fee: false }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const miField = field === 'services_no_shop';
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[12px] font-bold text-slate-700">{title}</div>
        <button type="button" onClick={add} className="text-[11px] font-semibold text-[#070B5F] flex items-center gap-1" data-testid={`${testId}-add`}>
          <Plus size={12} /> Add fee
        </button>
      </div>
      {items.length === 0 && <div className="text-[12px] text-slate-400 italic">No fees.</div>}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center mb-1.5">
          <Input className="col-span-3 h-8 text-[12px]" placeholder="Fee" value={it.name || ''} onChange={(e) => set(i, 'name', e.target.value)} aria-label="Fee name" data-testid={`${testId}-name-${i}`} />
          <Input className={`${miField ? 'col-span-2' : 'col-span-3'} h-8 text-[12px]`} placeholder="Paid to" value={it.paid_to || ''} onChange={(e) => set(i, 'paid_to', e.target.value)} aria-label="Paid to" />
          <MoneyInput className="col-span-2 h-8 text-[12px]" value={it.amount} onChange={(v) => set(i, 'amount', num(v))} aria-label="Amount" data-testid={`${testId}-amount-${i}`} />
          <label className="col-span-1 text-[10px] text-slate-500 flex items-center gap-1" title="Paid by the borrower; other payers are listed but not counted">
            <input type="checkbox" checked={it.borrower_paid !== false} onChange={(e) => set(i, 'borrower_paid', e.target.checked)} /> Borrower
          </label>
          <label className="col-span-1 text-[10px] text-slate-500 flex items-center gap-1" title="Counts toward the APR">
            <input type="checkbox" checked={!!it.is_apr_fee} onChange={(e) => set(i, 'is_apr_fee', e.target.checked)} /> APR
          </label>
          {miField && (
            <label className="col-span-1 text-[10px] text-slate-500 flex items-center gap-1" title="A mortgage insurance premium (upfront or single premium)">
              <input type="checkbox" checked={isMortgageInsurance(field, it)} onChange={(e) => set(i, 'mortgage_insurance', e.target.checked)} data-testid={`${testId}-mi-${i}`} /> MI
            </label>
          )}
          {isMortgageInsurance(field, it) ? (
            <span className="col-span-1 text-[10px] text-slate-400" title="Mortgage insurance follows its own QM rule (FHA, VA and USDA never count)" data-testid={`${testId}-qm-mi-${i}`}>MI rule</span>
          ) : (
            <label className="col-span-1 text-[10px] text-slate-500 flex items-center gap-1" title="Counts toward QM points and fees">
              <input type="checkbox" checked={countsTowardQm(field, it)} onChange={(e) => set(i, 'points_and_fees', e.target.checked)} data-testid={`${testId}-qm-${i}`} /> QM
            </label>
          )}
          <button type="button" onClick={() => remove(i)} className="col-span-1 text-slate-400 hover:text-red-600 justify-self-end" aria-label="Remove fee">
            <Trash2 size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

function MonthsRows({ title, rows, fees, upd }) {
  return (
    <div>
      <div className="text-[12px] font-bold text-slate-700 mb-1.5">{title}</div>
      {rows.map(([label, monthsKey, monthlyKey]) => (
        <div key={monthsKey} className="grid grid-cols-12 gap-2 items-center mb-1.5">
          <div className="col-span-5 text-[12px] text-slate-600">{label}</div>
          <NumberInput integer className="col-span-3 h-8 text-[12px]" value={fees[monthsKey]} onChange={(v) => upd(monthsKey, num(v))} aria-label={`${title} ${label} months`} suffix="mo" />
          <MoneyInput className="col-span-4 h-8 text-[12px]" value={fees[monthlyKey]} onChange={(v) => upd(monthlyKey, num(v))} aria-label={`${title} ${label} monthly`} />
        </div>
      ))}
    </div>
  );
}

// Stands in for the summary when the preview request fails, so stale totals never show.
const PREVIEW_FAILED = 'failed';

function Totals({ summary }) {
  if (summary === PREVIEW_FAILED) {
    return <div className="text-[12px] text-red-600" data-testid="fees-totals-error">Totals could not be worked out. Check the amounts, or reload the page.</div>;
  }
  if (!summary) return <div className="text-[12px] text-slate-400">Totals load once the fees do.</div>;
  const t = summary.totals;
  const ctc = summary.cash_to_close;
  const row = (label, amount, strong) => (
    <div key={label} className={`flex justify-between text-[12px] ${strong ? 'font-bold text-slate-900 border-t border-slate-200 pt-1 mt-1' : 'text-slate-600'}`}>
      <span>{label}</span><span>{usd(amount)}</span>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="fees-totals">
      <div>
        {summary.sections.map((s) => row(s.name, s.subtotal))}
        {row('D. Total Loan Costs (A+B+C)', t.D, true)}
        {row('I. Total Other Costs (E+F+G+H)', t.I, true)}
        {t.lender_credit ? row('Lender credits', -t.lender_credit) : null}
        <div data-testid="fees-total-j">{row('J. Total Closing Costs', t.J, true)}</div>
      </div>
      <div>
        <div className="text-[12px] font-bold text-slate-700 mb-1">Cash to close ({ctc.kind})</div>
        {ctc.lines.map((l) => row(l.label, l.amount))}
        {ctc.total == null
          ? <div className="text-[12px] text-amber-700 mt-1" data-testid="fees-ctc-missing">Add the {ctc.missing.join(' and ')} on the loan file to see cash to close.</div>
          : <div data-testid="fees-ctc-total">{row(ctc.total < 0 ? 'Cash to borrower' : 'Estimated cash to close', Math.abs(ctc.total), true)}</div>}
      </div>
      <QmTest qm={summary.qm} />
    </div>
  );
}

// QM points and fees (ticket 192, grill 186 D9), worked out by the server from the draft.
function QmTest({ qm }) {
  if (!qm) return null;
  if (qm.status == null) {
    return (
      <div className="border-t border-slate-200 pt-3" data-testid="fees-qm">
        <div className="text-[12px] font-bold text-slate-700 mb-1">QM points and fees</div>
        <div className="text-[12px] text-slate-500" data-testid="fees-qm-reason">{qm.reason}</div>
      </div>
    );
  }
  const pass = qm.status === 'pass';
  // A Non-QM program is shown for reference, so its result is never red.
  const badge = qm.reference_only ? 'bg-slate-100 text-slate-600' : pass ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700';
  const row = (label, value) => (
    <div key={label} className="flex justify-between text-[12px] text-slate-600"><span>{label}</span><span>{value}</span></div>
  );
  return (
    <div className="border-t border-slate-200 pt-3 space-y-1" data-testid="fees-qm">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-bold text-slate-700">QM points and fees</div>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge}`} data-testid="fees-qm-status">
          {pass ? 'Within limit' : 'Over limit'}
        </span>
      </div>
      {qm.items.map((i, n) => <div key={n}>{row(`${i.section}. ${i.name}`, usd(i.amount))}</div>)}
      <div data-testid="fees-qm-total">{row('Points and fees', `${usd(qm.points_and_fees)} (${qm.percent}%)`)}</div>
      {row('Total loan amount', usd(qm.total_loan_amount))}
      <div data-testid="fees-qm-limit">{row(`Limit (${qm.limit_label})`, usd(qm.limit))}</div>
      {row(pass ? 'Room left' : 'Over by', usd(Math.abs(qm.headroom)))}
      <p className="text-[11px] text-slate-500">
        {qm.limits_year} limits. Section A paid by the borrower counts. Tick QM on a third-party fee paid to you or an
        affiliate, or on lender-paid broker compensation; untick bona fide discount points.
      </p>
      {qm.limits_note && <p className="text-[11px] text-amber-700" data-testid="fees-qm-note">{qm.limits_note}</p>}
      {qm.reference_only && <p className="text-[11px] text-slate-500">{qm.reference_only}</p>}
    </div>
  );
}

// The Loan Estimate PDF (grill 186 Q27) is built from the saved fees, not the draft on screen.
function LePdfButton({ loan }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadWithAuth(losApi.lePdfPath(loan.loan_file_id), `loan-estimate-${loan.loan_number || loan.loan_file_id}.pdf`); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={download} disabled={busy} title="Built from the saved fees" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-[#070B5F] flex items-center gap-1.5 disabled:opacity-50" data-testid="fees-le-pdf">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Loan Estimate PDF
    </button>
  );
}

function FillFromTables({ loan, onFilled, current }) {
  const [open, setOpen] = useState(false);
  const [county, setCounty] = useState('');
  const [state, setState] = useState(loan.property_address?.state || '');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const fill = async () => {
    if (hasFees(current) && !(await confirmDialog({
      title: 'Replace the fees?',
      description: 'The fee tables replace every fee on this screen. Nothing is saved until you press Save.',
      confirmText: 'Replace',
    }))) return;
    setBusy(true);
    try {
      const r = await losApi.feesFromTables(loan.loan_file_id, { county: county || null, state: state || null });
      onFilled(r.le_fees);
      setNote(r.curated ? r.source_note : `No fee tables for ${r.state} yet; Texas figures are filled in as placeholders. ${r.source_note}`);
      setOpen(false);
    } catch (e) {
      toast({ title: 'Could not fill the fees', description: errorMessage(e) });
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-3" data-testid="fees-fill">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="text-[12px] font-semibold text-[#070B5F] flex items-center gap-1.5" data-testid="fees-fill-open">
          <Calculator size={13} /> Fill from fee tables
        </button>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Fld label="County" value={county} onChange={setCounty} placeholder="e.g. Travis" testId="fees-fill-county" />
          <StateSelect value={state} onChange={setState} testId="fees-fill-state" />
          <div className="flex gap-2">
            <button type="button" onClick={fill} disabled={busy || !state} title={state ? undefined : 'Pick the property state first'} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="fees-fill-go">
              {busy && <Loader2 size={12} className="animate-spin" />} Fill
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-slate-500">Cancel</button>
          </div>
        </div>
      )}
      {note && <p className="text-[11px] text-slate-500 mt-2" data-testid="fees-fill-note">{note} Review every line, then press Save.</p>}
    </div>
  );
}

export function LeFeesTab({ loan, onChange }) {
  const [fees, setFees] = useState(loan.le_fees || {});
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const locked = isAriveManaged(loan);
  useEffect(() => setFees(loan.le_fees || {}), [loan]);
  const upd = (k, v) => setFees(f => ({ ...f, [k]: v }));

  // Totals and cash to close for the draft, from the server so they match the PDF and MISMO.
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      losApi.previewFees(loan.loan_file_id, fees)
        .then((s) => { if (live) setSummary(s); })
        .catch(() => { if (live) setSummary(PREVIEW_FAILED); });
    }, PREVIEW_DELAY_MS);
    return () => { live = false; clearTimeout(t); };
  }, [loan, fees]);

  const save = async () => {
    setSaving(true);
    try { await losApi.updateLeFees(loan.loan_file_id, fees); toast({ title: 'Fees saved' }); onChange(); }
    catch (e) { if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Review Fees (LE)" onSave={locked ? undefined : save} saving={saving} extraButtons={<LePdfButton loan={loan} />} />
      <AriveLockNote loan={loan} testId="fees-arive-note">The fees are edited in Arive.</AriveLockNote>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {!locked && <FillFromTables loan={loan} current={fees} onFilled={setFees} />}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
            <AriveLocked loan={loan}>
              {LINE_SECTIONS.slice(0, 4).map(([key, field, title]) => (
                <FeeLines key={key} title={title} field={field} items={fees[field] || []} onChange={(v) => upd(field, v)} testId={`fees-${key}`} />
              ))}
              <div>
                <div className="text-[12px] font-bold text-slate-700 mb-1.5">F. Prepaids</div>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <Fld label="Prepaid interest days" kind="number" integer value={fees.prepaid_interest_days} onChange={(v) => upd('prepaid_interest_days', num(v))} />
                  <Fld label="Interest / day" kind="money" value={fees.prepaid_interest_per_day} onChange={(v) => upd('prepaid_interest_per_day', num(v))} />
                </div>
                <MonthsRows title="Prepaid" rows={PREPAID_ROWS} fees={fees} upd={upd} />
                {/* Ticket 258: private MI paid at closing (F, or a Section B MI line) counts
                    toward QM points and fees; a refundable premium counts only above FHA's
                    upfront premium. */}
                {hasMiAtClosing(fees) && !GOVERNMENT_PROGRAMS.includes(loan.loan_program) && (
                  <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
                    <input type="checkbox" checked={!!fees.mi_refundable} onChange={(e) => upd('mi_refundable', e.target.checked)} data-testid="fees-mi-refundable" />
                    Mortgage insurance is refunded pro rata, automatically, at payoff
                  </label>
                )}
              </div>
              <MonthsRows title="G. Initial Escrow Payment at Closing" rows={ESCROW_ROWS} fees={fees} upd={upd} />
              {LINE_SECTIONS.slice(4).map(([key, field, title]) => (
                <FeeLines key={key} title={title} field={field} items={fees[field] || []} onChange={(v) => upd(field, v)} testId={`fees-${key}`} />
              ))}
              <div className="max-w-xs">
                <Fld label="Lender credit" kind="money" value={fees.lender_credit} onChange={(v) => upd('lender_credit', num(v))} />
              </div>
            </AriveLocked>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 h-fit">
          <Totals summary={summary} />
        </div>
      </div>
    </div>
  );
}
